import {
  addBackLink,
  addTitleRow,
  applyColumnLayout,
  BODY_FONT,
  DATA_START_ROW as DETAIL_DATA_START_ROW,
  HEADER_FONT_12,
  HEADER_FONT_BOLD_11,
  HEADER_ROW as DETAIL_HEADER_ROW,
  hideColumnsAfter,
  hideRowsThrough,
  SPACER_ROW,
  TEMPLATE_ROW,
  setCell,
  setValueDataRow,
  setupSheetView,
  THIN,
  THIN_BLACK,
  VALUE_FILL,
} from "./detailSheetCommon";

export const COL_D = 4;
export const COL_E = 5;
export const COL_F = 6;
export const COL_G = 7;
export const COL_H = 8;
export const COL_I = 9;
export const COL_J = 10;
export const COL_K = 11;
export const COL_L = 12;

/** Legacy fills used by older detail sheets (Principle 2, 6, etc.) */
export const HEADER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFB9D1EA" },
};

export const TABLE_HEADER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFFCE4D6" },
};

/** Legacy row layout (rows 3–5) for sheets not yet migrated to detailSheetCommon */
export const TITLE_ROW = 3;
export const HEADER_ROW = 4;
export const DATA_START_ROW = 5;

const MEDIUM = { style: "medium", color: { argb: "FF000000" } };

const MONETARY_WIDTHS = {
  A: 9.1796875,
  B: 9.1796875,
  C: 9.1796875,
  D: 5.453125,
  E: 32.453125,
  F: 68.26953125,
  G: 27.26953125,
  H: 29.1796875,
  I: 38.1796875,
  J: 9.1796875,
  K: 9.1796875,
  L: 9.1796875,
};

const SETTLEMENT_WIDTHS = { ...MONETARY_WIDTHS, F: 64.1796875 };

const NON_MONETARY_WIDTHS = {
  A: 9.1796875,
  B: 9.1796875,
  C: 9.1796875,
  D: 5.453125,
  E: 60.7265625,
  F: 64.1796875,
  G: 29.1796875,
  H: 38.1796875,
  I: 9.1796875,
  J: 9.1796875,
  K: 9.1796875,
};

const PUNISHMENT_WIDTHS = { ...NON_MONETARY_WIDTHS, D: 7.1796875, F: 68.26953125 };

const APPEAL_WIDTHS = {
  A: 9.1796875,
  B: 9.1796875,
  C: 9.1796875,
  D: 14,
  E: 80.7265625,
  F: 62.1796875,
  G: 9.1796875,
  H: 9.1796875,
  I: 9.1796875,
};

const MONETARY_HEADERS = [
  { col: COL_D, label: "Sr. No." },
  { col: COL_E, label: "NGRBC Principle" },
  {
    col: COL_F,
    label: "Name of the regulatory/ enforcement agencies/ judicial institutions",
  },
  { col: COL_G, label: "Amount (In INR)" },
  { col: COL_H, label: "Brief of the Case" },
  { col: COL_I, label: "Has an appeal been preferred? (Yes/No)" },
];

const NON_MONETARY_HEADERS = [
  { col: COL_D, label: "Sr. No." },
  { col: COL_E, label: "NGRBC Principle" },
  {
    col: COL_F,
    label: "Name of the regulatory/ enforcement agencies/ judicial institutions",
  },
  { col: COL_G, label: "Brief of the Case" },
  { col: COL_H, label: "Has an appeal been preferred? (Yes/No)" },
];

const APPEAL_HEADERS = [
  { col: COL_D, label: "Sr. No.", bold: false },
  { col: COL_E, label: "Case Details", bold: true },
  {
    col: COL_F,
    label: "Name of the regulatory/ enforcement agencies/ judicial institutions",
    bold: true,
  },
];

function parseBorderSpec(spec) {
  const map = { t: THIN, m: MEDIUM };
  return {
    top: spec[0] !== "-" ? map[spec[0]] : undefined,
    left: spec[1] !== "-" ? map[spec[1]] : undefined,
    bottom: spec[2] !== "-" ? map[spec[2]] : undefined,
    right: spec[3] !== "-" ? map[spec[3]] : undefined,
  };
}

function applyRowBorderPattern(ws, row, colStart, patterns) {
  patterns.forEach((spec, index) => {
    ws.getCell(row, colStart + index).border = parseBorderSpec(spec);
  });
}

function applyMonetaryTitleBorders(ws, colEnd) {
  applyRowBorderPattern(ws, 8, COL_D, [
    "mmt-",
    "m-t-",
    "m-t-",
    "m-t-",
    "m-t-",
    "m-tm",
  ].slice(0, colEnd - COL_D + 1));
}

function applyProgrammesTitleBorders(ws, colEnd) {
  applyRowBorderPattern(ws, 8, COL_D, ["ttt-", "t-t-", "t-t-", "t-tt"].slice(0, colEnd - COL_D + 1));
}

function applyTableHeaderBorders(ws, row, colStart, colEnd) {
  const patterns = [];
  for (let col = colStart; col <= colEnd; col += 1) {
    patterns.push(col === colStart ? "tttt" : col === colStart + 1 ? "t-tt" : "tttt");
  }
  applyRowBorderPattern(ws, row, colStart, patterns);
}

function applyTemplateRowBorders(ws, row, colStart, colEnd) {
  for (let col = colStart; col <= colEnd; col += 1) {
    ws.getCell(row, col).border = THIN_BLACK;
  }
}

function applySpacerRowBorders(ws, row, colStart, colEnd) {
  const patterns = [];
  for (let col = colStart; col <= colEnd; col += 1) {
    if (col === colStart) patterns.push("ttt-");
    else if (col === colEnd) patterns.push("t-tt");
    else patterns.push("t-t-");
  }
  applyRowBorderPattern(ws, row, colStart, patterns);
}

function setupDetailSheet(
  ws,
  {
    widths,
    colStart,
    colEnd,
    backColEnd,
    topLeftCell = "D12",
    lastVisibleCol,
    xSplit = 3,
    zoomScale = 80,
  }
) {
  applyColumnLayout(ws, widths, ["A", "B"]);
  if (lastVisibleCol != null) hideColumnsAfter(ws, lastVisibleCol);
  setupSheetView(ws, { xSplit, ySplit: 11, topLeftCell, zoomScale });
  hideRowsThrough(ws, 5);
  addBackLink(ws, backColEnd);
}

function addHiddenTemplateRow(ws, colStart, colEnd, { height = 43.5 } = {}) {
  applyTemplateRowBorders(ws, TEMPLATE_ROW, colStart, colEnd);
  for (let col = colStart; col <= colEnd; col += 1) {
    setCell(ws, TEMPLATE_ROW, col, ".", {
      bordered: false,
      bold: col === colStart,
      font: BODY_FONT,
    });
  }
  ws.getRow(TEMPLATE_ROW).height = height;
  ws.getRow(TEMPLATE_ROW).hidden = true;
}

function addHeaderRow(ws, colStart, colEnd, headers, { height = 29 } = {}) {
  applyTableHeaderBorders(ws, DETAIL_HEADER_ROW, colStart, colEnd);
  ws.getRow(DETAIL_HEADER_ROW).height = height;
  headers.forEach(({ col, label }) => {
    setCell(ws, DETAIL_HEADER_ROW, col, label, {
      font: HEADER_FONT_12,
      bold: true,
      bordered: false,
      align: "center",
    });
  });
}

function addCustomSpacerRow(ws, colStart, colEnd, { height = 30 } = {}) {
  ws.mergeCells(SPACER_ROW, colStart, SPACER_ROW, colEnd);
  applySpacerRowBorders(ws, SPACER_ROW, colStart, colEnd);
  ws.getRow(SPACER_ROW).height = height;
}

function toYesNo(value) {
  if (value === true || value === "true" || value === "yes" || value === "Yes") return "Yes";
  if (value === false || value === "false" || value === "no" || value === "No") return "No";
  return ".";
}

function formatAmount(value) {
  if (value === null || value === undefined || value === "") return ".";
  const num = Number(value);
  if (!Number.isNaN(num)) {
    return Number.isInteger(num) ? String(num) : num.toFixed(2);
  }
  return String(value);
}

function formatNgrbcPrinciple(value) {
  if (value === null || value === undefined || value === "") return ".";
  const str = String(value).trim();
  if (/^P\d+$/i.test(str)) return str.replace(/^P/i, "Principle ");
  return str;
}

function displayText(value) {
  if (value === null || value === undefined || value === "") return ".";
  return String(value);
}

export function toRows(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data.filter((item) => item && typeof item === "object");
  if (typeof data === "object") {
    const keys = Object.keys(data);
    if (!keys.length) return [];
    const looksLikeRecord =
      "ngrbcPrinciple" in data ||
      "regulatoryAgency" in data ||
      "caseBrief" in data ||
      "regulatoryEnforcement" in data ||
      "caseDetails" in data;
    return looksLikeRecord ? [data] : keys.map((key) => data[key]).filter(Boolean);
  }
  return [];
}

function writeDataRows(ws, colStart, colEnd, rows, columnDefs, { rowHeight = 30 } = {}) {
  const dataRows = rows.length ? rows : [{}];
  dataRows.forEach((item, idx) => {
    const row = DETAIL_DATA_START_ROW + idx;
    setValueDataRow(ws, row, colStart, colEnd, item, columnDefs(item, idx));
    ws.getRow(row).height = rowHeight;
  });
}

export function buildMonetaryDetailSheet(
  workbook,
  sheetName,
  title,
  rows,
  { widths = MONETARY_WIDTHS, titleBorderStyle = "monetary", headers = MONETARY_HEADERS } = {}
) {
  const ws = workbook.addWorksheet(sheetName, { views: [{ showGridLines: false }] });
  const colEnd = COL_I;

  setupDetailSheet(ws, {
    widths,
    colStart: COL_D,
    colEnd,
    backColEnd: COL_I,
    lastVisibleCol: COL_L,
  });

  addTitleRow(ws, COL_D, colEnd, title, { height: 30 });
  if (titleBorderStyle === "monetary") applyMonetaryTitleBorders(ws, colEnd);

  addHeaderRow(ws, COL_D, colEnd, headers);
  addHiddenTemplateRow(ws, COL_D, colEnd);
  addCustomSpacerRow(ws, COL_D, colEnd);

  writeDataRows(ws, COL_D, colEnd, rows, (item, idx) => [
    { col: COL_D, value: String(idx + 1), align: "center", fill: undefined, font: HEADER_FONT_BOLD_11 },
    { col: COL_E, value: formatNgrbcPrinciple(item?.ngrbcPrinciple), align: "left" },
    { col: COL_F, value: displayText(item?.regulatoryAgency), align: "left" },
    { col: COL_G, value: formatAmount(item?.amountInINR), align: "right" },
    { col: COL_H, value: displayText(item?.caseBrief), align: "left", fill: undefined },
    { col: COL_I, value: toYesNo(item?.hasAppealPreferred), align: "center" },
  ]);

  hideRowsThrough(ws, 5);
  return ws;
}

export function buildNonMonetaryDetailSheet(
  workbook,
  sheetName,
  title,
  rows,
  { widths = NON_MONETARY_WIDTHS, headerHeight = 29 } = {}
) {
  const ws = workbook.addWorksheet(sheetName, { views: [{ showGridLines: false }] });
  const colEnd = COL_H;

  setupDetailSheet(ws, {
    widths,
    colStart: COL_D,
    colEnd,
    backColEnd: COL_H,
    lastVisibleCol: COL_K,
  });

  addTitleRow(ws, COL_D, colEnd, title, { height: 30 });
  applyMonetaryTitleBorders(ws, colEnd);

  addHeaderRow(ws, COL_D, colEnd, NON_MONETARY_HEADERS, { height: headerHeight });
  addHiddenTemplateRow(ws, COL_D, colEnd);
  addCustomSpacerRow(ws, COL_D, colEnd);

  writeDataRows(ws, COL_D, colEnd, rows, (item, idx) => [
    { col: COL_D, value: String(idx + 1), align: "center", fill: undefined, font: HEADER_FONT_BOLD_11 },
    { col: COL_E, value: formatNgrbcPrinciple(item?.ngrbcPrinciple), align: "left" },
    { col: COL_F, value: displayText(item?.regulatoryAgency), align: "left" },
    { col: COL_G, value: displayText(item?.caseBrief), align: "left", fill: undefined },
    { col: COL_H, value: toYesNo(item?.hasAppealPreferred), align: "center" },
  ]);

  hideRowsThrough(ws, 5);
  return ws;
}

export function buildAppealDetailSheet(workbook, sheetName, title, rows) {
  const ws = workbook.addWorksheet(sheetName, { views: [{ showGridLines: false }] });
  const colEnd = COL_F;

  setupDetailSheet(ws, {
    widths: APPEAL_WIDTHS,
    colStart: COL_D,
    colEnd,
    backColEnd: COL_F,
    lastVisibleCol: COL_I,
  });

  addTitleRow(ws, COL_D, colEnd, title, { height: 30 });
  for (let col = COL_D; col <= colEnd; col += 1) {
    ws.getCell(8, col).border = THIN_BLACK;
  }

  applyTableHeaderBorders(ws, DETAIL_HEADER_ROW, COL_D, colEnd);
  ws.getRow(DETAIL_HEADER_ROW).height = 15.5;
  APPEAL_HEADERS.forEach(({ col, label, bold = true }) => {
    setCell(ws, DETAIL_HEADER_ROW, col, label, {
      font: HEADER_FONT_12,
      bold,
      bordered: false,
      align: "center",
    });
  });

  applyTemplateRowBorders(ws, TEMPLATE_ROW, COL_D, colEnd);
  for (let col = COL_D; col <= colEnd; col += 1) {
    setCell(ws, TEMPLATE_ROW, col, ".", { bordered: false, font: BODY_FONT });
  }
  ws.getRow(TEMPLATE_ROW).height = 29;
  ws.getRow(TEMPLATE_ROW).hidden = true;

  addCustomSpacerRow(ws, COL_D, colEnd);

  writeDataRows(ws, COL_D, colEnd, rows, (item, idx) => [
    { col: COL_D, value: String(idx + 1), align: "center", fill: undefined, font: BODY_FONT },
    { col: COL_E, value: displayText(item?.caseDetails), align: "left" },
    { col: COL_F, value: displayText(item?.regulatoryEnforcement), align: "left" },
  ], { rowHeight: undefined });

  hideRowsThrough(ws, 5);
  return ws;
}

export { SETTLEMENT_WIDTHS, PUNISHMENT_WIDTHS };
